import classNames from "classnames";
import { Link } from "../../common/components/Link";
import { Icon } from "../../common/components/icon/Icon";
import { IconType } from "../../common/components/icon/IconType";
import type ComponentTypes from "../componentTypes";
import { type CMSTeasersProps } from "./CMSTeaserContainer/CMSTeasers";
import { type CMSTeaserProps } from "../model";
import { type CMSBannerProps } from "./CMSBanner";
import { type CMSRotatingBannersProps } from "./CMSRotatingBanners";
import {
  type CMSAccordionContainerDTO,
  type CMSAccordionDTO,
  type CMSColumnDTO,
  type CMSColumnLinksDTO,
  type CMSColumnsContainerDTO,
  type CMSPhotoDTO,
  type CMSPhotoGalleryDTO,
  type CMSQuoteDTO,
  type CMSStandaloneImageDTO,
  type CMSTableDTO,
  type LoginCallDTO,
} from "../dto";
import { type CMSFilterableTileContainerDTO } from "./CMSFilterableTileContainer/dto";
import { renderCMSComponent } from "../componentFactory";
import { type BEButtonSizes, getButtonSize, getButtonType } from "~/CMSComponents/CMSComponentHelpers";
import { Button } from "~/common/components/Button/Button";
import type { CMSPageNavigationComponentProps } from "~/CMSComponents/components/CMSPageNavigation";
import type { CMSNewsletterConsentDTO } from "~/CMSComponents/components/CMSNewsletterConsent";
import type { EwellixPageNavigationComponentDTO } from "~/CMSComponents/components/EwellixPageNavigationComponent/types";
import { Stack } from "@mui/material";

export type CMSComponentProps =
  | CMSSectionComponentProps
  | CMSStandaloneImageDTO
  | CMSParagraphProps
  | CMSListProps
  | CMSButtonProps
  | CMSLinkProps
  | CMSTeasersProps
  | CMSTeaserProps
  | CMSBannerProps
  | CMSRotatingBannersProps
  | CMSFilterableTileContainerDTO
  | LoginCallDTO
  | CMSQuoteDTO
  | CMSColumnsContainerDTO
  | CMSColumnDTO
  | CMSPageNavigationComponentProps
  | CMSNewsletterConsentDTO
  | CMSPhotoDTO
  | CMSPhotoGalleryDTO
  | CMSColumnLinksDTO
  | CMSAccordionContainerDTO
  | CMSAccordionDTO
  | CMSTableDTO
  | EwellixPageNavigationComponentDTO;

export interface CMSProps {
  id: string;
  className?: string;
  content?: string;
  fullWidth?: boolean;
}

export interface CMSParagraphProps extends CMSProps {
  type: ComponentTypes.CMSParagraph;
}

export interface CMSListProps extends CMSProps {
  type: ComponentTypes.CMSList;
  subHeadline?: string;
}

export interface CMSButtonProps extends CMSProps {
  type: ComponentTypes.CMSButton;
  buttonType?: "PRIMARY" | "SECONDARY" | "TEXT";
  url?: string;
  label?: string;
  size?: BEButtonSizes;
  openInExternal?: boolean;
}

export interface CMSLinkProps extends CMSProps {
  type: ComponentTypes.CMSLink;
  url?: string;
  title?: string;
  target?: string;
  label?: string;
}

export interface CMSSectionComponentProps extends CMSProps {
  id: string;
  type: ComponentTypes.CMSSection;
  headlineTag?: string;
  htmlId?: string;
  headline?: string;
  subHeadline?: string;
  subListHeadline?: string;
  elements: CMSComponentProps[];
}

/**
 * @see https://reactjs.org/docs/dom-elements.html#dangerouslysetinnerhtml
 * @param html
 */
export function createMarkup(html: string) {
  html = cleanupText(html);
  return { __html: html };
}

export function cleanupText(text: string): string;
export function cleanupText(text: undefined): undefined;
export function cleanupText(text: string | undefined): string | undefined;
export function cleanupText(text: null): null;
export function cleanupText(text: string | undefined | null) {
  return text ? text.replace(/\\([()])/g, "$1") : text;
}

export function renderAsHTML(html: string) {
  return <div className={"scha__cmssection__html"} dangerouslySetInnerHTML={createMarkup(html)} />;
}

export const CMSParagraph = ({ content = "", className }: CMSParagraphProps) => {
  return (
    <div
      className={classNames(
        "scha__cmssection__html scha__cmssection__sub--paragraph copy_left jsp__user-generated-content",
        className,
      )}
      dangerouslySetInnerHTML={createMarkup(content)}
    />
  );
};

export const CMSList = (props: CMSListProps) => {
  const { content, subHeadline, className } = props;
  return (
    <div className={classNames("scha__cms scha__cmssection__sub--list", className)}>
      {/* This h3 is styled like a h6 in the designs */}
      {subHeadline && !!subHeadline.length && (
        <h3
          className={"scha__cmssection__sub-element__headline headline_h6_left"}
          dangerouslySetInnerHTML={createMarkup(subHeadline)}
        />
      )}
      {content && !!content.length && renderAsHTML(content)}
    </div>
  );
};

export const CMSButton = (props: CMSButtonProps) => {
  const { url, label, className, buttonType, size, openInExternal } = props;

  const buttonProps = {
    ...getButtonSize(size ?? "BIG"),
    ...getButtonType(buttonType ?? "primary"),
  };

  // @ts-ignore
  return (
    <div className="scha__cms">
      <Button
        component={"a"}
        className={classNames("scha__cmssection__sub--button scha__cmssection__button", className)}
        href={url}
        {...buttonProps}
        target={openInExternal ? "_blank" : undefined}
      >
        {label}
      </Button>
    </div>
  );
};

export const CMSLink = (props: CMSLinkProps) => {
  const { url, label, target, className } = props;
  return (
    <Link
      className={classNames("scha__cms scha__cmssection__sub--link", className)}
      href={url}
      target={target}
      iconBefore={<Icon className={"scha__link--arrow-link"} type={IconType.ChevronRight} />}
    >
      {label}
    </Link>
  );
};

export const CMSSection = ({
  headlineTag,
  elements,
  headline,
  subHeadline,
  htmlId,
  className,
}: CMSSectionComponentProps) => {
  const HeadlineTag = (headlineTag?.trim() ?? "p") as keyof JSX.IntrinsicElements;

  return (
    <div className={classNames("scha__cms scha__cmssection js__cms-section", className)}>
      {headline && !!headline.length && (
        <HeadlineTag
          id={htmlId}
          className={classNames("scha__cmssection__headline headline_h2_left headline_h2_left--default")}
          dangerouslySetInnerHTML={createMarkup(headline)}
        />
      )}
      {subHeadline && !!subHeadline.length && (
        <p
          className={"scha__cmssection__headline headline_h2_left headline_h2_left--label"}
          dangerouslySetInnerHTML={createMarkup(subHeadline)}
        />
      )}
      {elements && !!elements.length && (
        <Stack
          className={"scha__cmssection__sub-elements"}
          sx={{
            gap: 3,
          }}
        >
          {elements.map((el, index) => renderCMSComponent(el, undefined, index))}
        </Stack>
      )}
    </div>
  );
};
